import { computed, Injectable, signal } from '@angular/core';
import { ISelectedTransactionPosition } from '../interfaces/ISelectedTransactionPosition';
import { ITransaction } from '../interfaces/ITransaction';
import { ITransactionPosition } from '../interfaces/ITransactionPosition';
import { collectPrintableTicketNumbers } from '../utils/collect-printable-ticket-numbers';

@Injectable({
  providedIn: 'root'
})
export class TransactionsStore {

  public readonly selectedShowTransactions = signal<ITransaction[]>([]);
  public readonly contextTransactions = signal<ITransaction[]>([]);
  public readonly selectionModeActive = signal(false);
  public readonly selectedPositionKeys = signal<Set<string>>(new Set());

  public readonly hasSelection = computed(() => this.selectedPositionKeys().size > 0);

  public readonly selectedPositions = computed((): ISelectedTransactionPosition[] => {
    const keys = this.selectedPositionKeys();
    const result: ISelectedTransactionPosition[] = [];

    for (const transaction of this.contextTransactions()) {
      for (const position of transaction.transactionPositions) {
        const key = TransactionsStore.getPositionKey(transaction.transactionId, position.id);

        if (keys.has(key)) {
          result.push({ transactionId: transaction.transactionId, position });
        }
      }
    }

    return result;
  });

  public readonly selectedPositionsByTransaction = computed(() => {
    const grouped = new Map<number, ITransactionPosition[]>();

    for (const { transactionId, position } of this.selectedPositions()) {
      const positions = grouped.get(transactionId) ?? [];
      positions.push(position);
      grouped.set(transactionId, positions);
    }

    return grouped;
  });

  public readonly selectedPrintableTicketNumbers = computed(() =>
    collectPrintableTicketNumbers(this.selectedPositions().map(selected => selected.position))
  );

  public static getPositionKey(transactionId: number, positionId: number): string {
    return `${transactionId}:${positionId}`;
  }

  public toggleSelectionMode(): void {
    if (this.selectionModeActive()) {
      this.selectionModeActive.set(false);
      this.clearSelection();
    } else {
      this.selectionModeActive.set(true);
    }
  }

  public togglePositionKey(transactionId: number, positionId: number): void {
    const key = TransactionsStore.getPositionKey(transactionId, positionId);

    this.selectedPositionKeys.update(keys => {
      const next = new Set(keys);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }

  public toggleTransaction(transactionId: number, positions: ITransactionPosition[]): void {
    const keys = positions.map(p => TransactionsStore.getPositionKey(transactionId, p.id));
    const allSelected = keys.every(key => this.selectedPositionKeys().has(key));

    this.selectedPositionKeys.update(selectedKeys => {
      const next = new Set(selectedKeys);

      if (allSelected) {
        keys.forEach(key => next.delete(key));
      } else {
        keys.forEach(key => next.add(key));
      }

      return next;
    });
  }

  public clearSelection(): void {
    this.selectedPositionKeys.set(new Set());
  }

  public isPositionKeySelected(transactionId: number, positionId: number): boolean {
    return this.selectedPositionKeys().has(TransactionsStore.getPositionKey(transactionId, positionId));
  }

  public isTransactionFullySelected(transactionId: number, positions: ITransactionPosition[]): boolean {
    return positions.length > 0
      && positions.every(position => this.isPositionKeySelected(transactionId, position.id));
  }

  public isTransactionPartiallySelected(transactionId: number, positions: ITransactionPosition[]): boolean {
    const selectedCount = positions
      .filter(position => this.isPositionKeySelected(transactionId, position.id))
      .length;

    return selectedCount > 0 && selectedCount < positions.length;
  }
}
